import axios from 'axios';

// Use relative URL in production, localhost in development
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : '/api';

console.log('[API] API_URL configured as:', API_URL);

const api = axios.create({
  baseURL: API_URL,
});

// Agregar token a todas las peticiones
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authenticateWithAzure = (accessToken) => {
  return api.post('/auth/azure', { accessToken });
};

export const getCurrentUser = () => {
  return api.get('/auth/me');
};

// Users
export const getUsers = () => {
  return api.get('/users/list');
};

export const getAllUsers = () => {
  return api.get('/users');
};

export const getDevOpsMembers = () => {
  return api.get('/users/devops');
};

export const updateUserRole = (userId, role) => {
  return api.put(`/users/${userId}/role`, { role });
};

// Events
export const getEvents = (start, end) => {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  return api.get('/events', { params });
};

export const createEvent = (eventData) => {
  return api.post('/events', eventData);
};

export const updateEvent = (id, eventData) => {
  return api.put(`/events/${id}`, eventData);
};

export const deleteEvent = (id) => {
  return api.delete(`/events/${id}`);
};

// Team Events
export const getTeamEvents = (start, end) => {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  return api.get('/team-events', { params });
};

export const createTeamEvent = (eventData) => {
  return api.post('/team-events', eventData);
};

export const updateTeamEvent = (id, eventData) => {
  return api.put(`/team-events/${id}`, eventData);
};

export const deleteTeamEvent = (id) => {
  return api.delete(`/team-events/${id}`);
};

// Guardias Report
export const sendGuardiasReport = (month, year, recipients) => {
  return api.post('/guardias/reporte', { month, year, recipients });
};

// Contactos de guardia
export const getGuardiaContacts = () => {
  return api.get('/guardia-contacts');
};

export const createGuardiaContact = (contact) => {
  return api.post('/guardia-contacts', contact);
};

export const updateGuardiaContact = (id, contact) => {
  return api.put(`/guardia-contacts/${id}`, contact);
};

export const deleteGuardiaContact = (id) => {
  return api.delete(`/guardia-contacts/${id}`);
};

// Version
export const getVersion = () => {
  return api.get('/version');
};

// System Config
export const getAllSystemConfig = () => {
  return api.get('/config');
};

export const getSystemConfig = (key) => {
  return api.get(`/config/${key}`);
};

export const updateSystemConfig = (key, value) => {
  return api.put(`/config/${key}`, { value });
};

// Weekly Reports (Redmine)
export const getWeeklyReports = (weeks = 4) => {
  return api.get('/weekly-reports', { params: { weeks } });
};

export const reloadWeeklyReports = (weeks = 4) => {
  return api.post('/weekly-reports/reload', { weeks });
};

export const getSyncStatus = () => {
  return api.get('/weekly-reports/sync-status');
};

export const getWeeklyReportBreakdown = (weeklyReportId) => {
  return api.get(`/weekly-reports/${weeklyReportId}/breakdown`);
};

// Enchufeitor (Redmine Issue Creation)
export const getRedmineProjects = () => {
  return api.get('/enchufeitor/projects');
};

export const getRedminePriorities = () => {
  return api.get('/enchufeitor/priorities');
};

export const createRedmineIssue = (issueData) => {
  return api.post('/enchufeitor/create-issue', issueData);
};

// Client Hours
export const getClientHours = (year, month) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  return api.get('/client-hours', { params });
};

export const refreshClientHours = (year, month) => {
  return api.post('/client-hours/refresh', { year, month });
};

export const getAIAutomation = ({ year, month, scope } = {}) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  if (scope) params.scope = scope;
  return api.get('/ai-automation', { params });
};

export const getMTTR = ({ year, month, scope } = {}) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  if (scope) params.scope = scope;
  return api.get('/mttr', { params });
};

export const getProactivity = ({ year, month, scope } = {}) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  if (scope) params.scope = scope;
  return api.get('/proactivity', { params });
};

export const getAnnualClientHours = (year) => {
  const params = {};
  if (year) params.year = year;
  return api.get('/client-hours/annual', { params });
};

export const getClientTimeEntries = (projectId, year, month) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  return api.get(`/client-hours/${projectId}/entries`, { params });
};

export const getClientWikiUrl = (projectId, projectIdentifier, projectName) => {
  const params = {};
  if (projectIdentifier) params.projectIdentifier = projectIdentifier;
  if (projectName) params.projectName = projectName;
  return api.get(`/client-hours/${projectId}/wiki-url`, { params });
};

// Imputaciones diarias
export const getMissingImputaciones = () => {
  return api.get('/imputaciones/missing');
};

// AI Assistant (Helpdesk)
export const getAssistantConfig = () => {
  return api.get('/assistant/config');
};

export const updateAssistantConfig = (config) => {
  return api.put('/assistant/config', config);
};

export const getAssistantStatus = () => {
  return api.get('/assistant/status');
};

export const getAssistantConversation = () => {
  return api.get('/assistant/conversation');
};

export const getAssistantMessages = (conversationId) => {
  return api.get(`/assistant/conversation/${conversationId}/messages`);
};

export const sendAssistantMessage = (conversationId, message) => {
  return api.post('/assistant/message', { conversationId, message });
};

export const resetAssistantSession = (conversationId) => {
  return api.post('/assistant/reset-session', { conversationId });
};

export default api;
